import { ArrowDownIcon, ArrowUpIcon } from "@heroicons/react/24/outline";
import { DeleteButton, IconButton } from "components/ui/action-button";
import { ChoiceTileOption, ChoiceTiles } from "components/ui/choice-tiles";
import { InlineBanner } from "components/ui/inline-banner";
import { GetWorkflowSettingsResult } from "generated/graphql";
import React from "react";
import { actionIntervalSource } from "../delay-recipe-editor";
import { EmailSenderEditor } from "../email-action-editor";
import { entityTypeNoun } from "../manage-workflows-helper";
import { SmsActionEditor, WorkflowPhoneLine } from "../sms-action-editor";
import { TaskConfigurationEditor, TaskNotificationEditor } from "../task-action-editor";
import { EditableWorkflow } from "../types";
import { WorkflowSettingsField } from "../workflow-settings-field";
import { ActionIntervalEditor } from "./action-interval-editor";
import { ActionTimingEditor } from "./action-timing-editor";
import {
    actionChipKind,
    actionKindSummary,
    sectionNumber,
    smsActionBlocker,
    WizardActionKind,
    wizardActionSections,
    WizardSectionEditor,
    WizardSectionItem,
    WorkflowActionChip,
    workflowActionChips,
    workflowActionSettingSuffix,
} from "./builder-helper";
import { ActionChip, ActionKindIcon } from "./sequence-strip";
import { WizardSection } from "./wizard-section";
import { ActionMoveDirection } from "./workflow-blueprint";

type WorkflowSetting = EditableWorkflow["settings"][number];
type EditableWorkflowAction = EditableWorkflow["actions"][number];

interface WorkflowActionCardProps {
    workflow: EditableWorkflow;
    workflowSettings: GetWorkflowSettingsResult;
    action: EditableWorkflowAction;
    position: number;
    actionCount: number;
    settings: WorkflowSetting[];
    timeline: string | null;
    editable: boolean;
    isDraft: boolean;
    canEditStructure: boolean;
    selectedActionKey: string | null;
    actionKindOptions: ChoiceTileOption<string>[];
    showEmailSender: boolean;
    phoneLines: WorkflowPhoneLine[];
    phoneLinesLoaded: boolean;
    onSelectKind: (key: string) => void;
    onMove: (direction: ActionMoveDirection) => void;
    onRemove: () => void;
    onCommitTiming: (delayRecipe: string) => Promise<void>;
    onChangeAction: (workflow: EditableWorkflow) => void;
    onSettingSaved: (settingKey: string) => void;
}

const timingVerb = (kind: WizardActionKind): string => {
    switch (kind) {
        case "task":
            return "Create";
        case "email":
        case "sms":
            return "Send";
    }
};

const settingWithKey = (
    settings: WorkflowSetting[],
    key: string,
): WorkflowSetting | null => settings.find(setting => setting.key === key) ?? null;

interface ActionStructureControlsProps {
    position: number;
    actionCount: number;
    onMove: (direction: ActionMoveDirection) => void;
    onRemove: () => void;
}

const ActionStructureControls: React.FC<ActionStructureControlsProps> = props => {
    const { position, actionCount, onMove, onRemove } = props;

    return (
        <div className="ml-auto flex items-center gap-1">
            <IconButton
                Icon={ArrowUpIcon}
                label="Move action up"
                backgroundColor="bg-gray-100"
                size="sm"
                disabled={position === 0}
                onClick={() => onMove("up")}
            />
            <IconButton
                Icon={ArrowDownIcon}
                label="Move action down"
                backgroundColor="bg-gray-100"
                size="sm"
                disabled={position === actionCount - 1}
                onClick={() => onMove("down")}
            />
            <DeleteButton
                backgroundColor="bg-gray-100"
                size="sm"
                confirm={{
                    title: "Remove action?",
                    message:
                        "This action and its settings will be removed from the workflow.",
                    confirmLabel: "Yes, remove",
                }}
                onClick={onRemove}
            />
        </div>
    );
};

interface ActionCardHeaderProps extends ActionStructureControlsProps {
    chip: WorkflowActionChip | null;
    canEditStructure: boolean;
}

const ActionCardHeader: React.FC<ActionCardHeaderProps> = props => {
    const { position, actionCount, chip, canEditStructure, onMove, onRemove } = props;

    return (
        <div className="flex flex-wrap items-center gap-3 rounded-t-lg border-b border-gray-200 bg-gray-100 px-4 py-3">
            <span className="h-5 w-1 shrink-0 rounded-full bg-brand-500" />
            <h3 className="text-lg font-medium text-gray-900">Action {position + 1}</h3>
            {chip ? <ActionChip chip={chip} /> : null}
            {canEditStructure ? (
                <ActionStructureControls
                    position={position}
                    actionCount={actionCount}
                    onMove={onMove}
                    onRemove={onRemove}
                />
            ) : null}
        </div>
    );
};

export const WorkflowActionCard: React.FC<WorkflowActionCardProps> = props => {
    const {
        workflow,
        workflowSettings,
        action,
        position,
        actionCount,
        settings,
        timeline,
        editable,
        isDraft,
        canEditStructure,
        selectedActionKey,
        actionKindOptions,
        showEmailSender,
        phoneLines,
        phoneLinesLoaded,
        onSelectKind,
        onMove,
        onRemove,
        onCommitTiming,
        onChangeAction,
        onSettingSaved,
    } = props;

    const actionId = action.id;
    const kind = actionChipKind(action.__typename);
    const chip = workflowActionChips(workflow)[position];
    const sections = wizardActionSections(
        { kind, suffix: workflowActionSettingSuffix(action), position },
        settings.map(setting => setting.key),
        entityTypeNoun(workflow.attachTo),
    );
    const summary = actionKindSummary(kind, actionKindOptions, selectedActionKey);
    const smsBlocker =
        kind === "sms"
            ? smsActionBlocker({
                  loaded: phoneLinesLoaded,
                  phoneLineCount: phoneLines.length,
              })
            : null;

    const renderEditor = (editor: WizardSectionEditor): React.ReactNode => {
        switch (editor) {
            case "action-kind":
                return (
                    <div className="space-y-4">
                        {canEditStructure ? (
                            <ChoiceTiles
                                options={actionKindOptions}
                                columns={2}
                                value={selectedActionKey}
                                onSelect={onSelectKind}
                            />
                        ) : (
                            <div className="flex items-start gap-2.5">
                                <ActionKindIcon
                                    kind={summary.kind}
                                    className="w-5 h-5 text-gray-500 flex-shrink-0"
                                />
                                <div>
                                    <p className="text-sm font-medium text-gray-800">
                                        {summary.title}
                                    </p>
                                    {summary.description ? (
                                        <p className="text-sm text-gray-500">
                                            {summary.description}
                                        </p>
                                    ) : null}
                                </div>
                            </div>
                        )}
                        {editable && !isDraft ? (
                            <p className="text-xs text-gray-400">
                                Unpublish this workflow to change what this action does.
                            </p>
                        ) : null}
                        {position === 0 ? (
                            <ActionTimingEditor
                                workflow={workflow}
                                verb={timingVerb(kind)}
                                timeline={timeline}
                                onCommit={onCommitTiming}
                            />
                        ) : actionId ? (
                            <ActionIntervalEditor
                                source={actionIntervalSource(workflow, actionId)}
                                verb={timingVerb(kind)}
                                timeline={timeline}
                                onCommit={onCommitTiming}
                            />
                        ) : null}
                    </div>
                );
            case "email-sender":
                return showEmailSender ? (
                    <EmailSenderEditor workflow={workflow} onChange={onChangeAction} />
                ) : (
                    <p className="text-sm text-gray-500">
                        Sent by the same user as the first email action.
                    </p>
                );
            case "sms-sender":
                return (
                    <div className="space-y-4">
                        {smsBlocker ? (
                            <InlineBanner type="warning">{smsBlocker}</InlineBanner>
                        ) : null}
                        {actionId ? (
                            <SmsActionEditor
                                workflow={workflow}
                                actionId={actionId}
                                onChange={onChangeAction}
                                phoneLines={phoneLines}
                                phoneLinesLoaded={phoneLinesLoaded}
                            />
                        ) : null}
                    </div>
                );
            case "task-details":
                return actionId ? (
                    <TaskConfigurationEditor
                        workflow={workflow}
                        actionId={actionId}
                        onChange={onChangeAction}
                    />
                ) : null;
            case "task-notification":
                return actionId ? (
                    <TaskNotificationEditor
                        workflow={workflow}
                        actionId={actionId}
                        onChange={onChangeAction}
                    />
                ) : null;
        }
    };

    const renderItem = (item: WizardSectionItem): React.ReactNode => {
        if (item.type === "editor") {
            return (
                <React.Fragment key={item.editor}>
                    {renderEditor(item.editor)}
                </React.Fragment>
            );
        }
        const setting = settingWithKey(settings, item.settingKey);
        if (!setting) {
            return null;
        }
        return (
            <WorkflowSettingsField
                key={`${workflow.id}.tenant.${setting.key}`}
                workflow={workflow}
                setting={setting}
                label={item.label === "hidden" ? "" : setting.label}
                workflowSettings={workflowSettings.tenant}
                onSaved={() => onSettingSaved(setting.key)}
                locked={!!smsBlocker}
            />
        );
    };

    const sectionCards = sections.map((section, index) => (
        <WizardSection
            key={section.key}
            number={sectionNumber(position, index, actionCount)}
            title={section.title}
            description={section.description}>
            {section.items.map(renderItem)}
        </WizardSection>
    ));

    if (actionCount === 1) {
        return (
            <section
                aria-label={`Action ${position + 1}`}
                className="divide-y divide-gray-200">
                {sectionCards}
            </section>
        );
    }

    return (
        <section
            aria-label={`Action ${position + 1}`}
            className="rounded-lg bg-white ring-1 ring-gray-300">
            <ActionCardHeader
                position={position}
                actionCount={actionCount}
                chip={chip ?? null}
                canEditStructure={canEditStructure}
                onMove={onMove}
                onRemove={onRemove}
            />
            <div className="divide-y divide-gray-200 px-4 py-5">{sectionCards}</div>
        </section>
    );
};
